// src/pages/LandingPage.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import '../styles/LandingPage.css';

function LandingPage() {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate('/register');
  };

  return (
    <div className="landing-page">
      <Helmet>
        <title>AutoBlogPilot - AI-Powered Content Automation for Your Blog</title>
        <meta name="description" content="Automate your blog with AI-powered content. Import your existing blog, maintain your unique voice, and let our AI create SEO-optimized content that automatically posts to WordPress." />
        
        {/* Favicon */}
        <link rel="icon" type="image/x-icon" href="/favicon.ico" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <link rel="manifest" href="/manifest.json" />
        <meta name="theme-color" content="#ffffff" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://autoblogpilot.com/" />
        <meta property="og:title" content="AutoBlogPilot - AI-Powered Content Automation" />
        <meta property="og:description" content="Automate your blog with AI-powered content. Import your existing blog, maintain your unique voice, and let our AI create SEO-optimized content." />
        <meta property="og:image" content="https://autoblogpilot.com/og-image.png" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://autoblogpilot.com/" />
        <meta property="twitter:title" content="AutoBlogPilot - AI-Powered Content Automation" />
        <meta property="twitter:description" content="Automate your blog with AI-powered content. Import your existing blog, maintain your unique voice, and let our AI create SEO-optimized content." />
        <meta property="twitter:image" content="https://autoblogpilot.com/og-image.png" />

        {/* Additional SEO meta tags */}
        <meta name="keywords" content="AI blog automation, content automation, WordPress automation, SEO optimization, AI content generation" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://autoblogpilot.com/" />
      </Helmet>

      <nav className="navbar">
        <a href="/">
          <div className="logo">
            <img src="/autoblogpilotlogo.png" alt="AutoBlogPilot Logo" className="logo-image" />
          </div>
        </a>
        <div className="nav-links">
          <a href="#features">Features</a>
          <a href="#how-it-works">How it Works</a>
          <a href="#pricing">Pricing</a>
          <button className="login-btn" onClick={() => navigate('/login')}>Login</button>
        </div>
      </nav>

      <main>
        <section className="hero">
          <div className="hero-content">
            <h1>Automate Your Blog with <span className="highlight">AI-Powered Content</span></h1>
            <p className="subtitle">
              Import your existing blog, maintain your unique voice, and let our AI create 
              SEO-optimized content that automatically posts to your WordPress site.
            </p>
            <div className="cta-group">
              <button className="cta-primary" onClick={handleGetStarted}>
                Get Started for Free
              </button>
              <button className="cta-secondary" onClick={() => navigate('/demo')}>
                Watch Demo
              </button>
            </div>
          </div>
          <div className="hero-image">
            <img 
              src="/platform-preview.png" 
              alt="AutoBlogPilot platform interface showing content automation" 
            />
          </div>
        </section>

        <section className="features" id="features">
          <h2>Key Features</h2>
          <div className="feature-grid">
            <div className="feature-card">
              <div className="feature-icon">🔄</div>
              <h3>Blog Import & Analysis</h3>
              <p>Seamlessly import your existing blog posts and analyze your writing style</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🤖</div>
              <h3>AI Content Creation</h3>
              <p>Generate content that matches your voice and maintains quality</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📈</div>
              <h3>SEO Optimization</h3>
              <p>Every post is automatically optimized for search engines</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📅</div>
              <h3>Auto Publishing</h3>
              <p>Schedule and publish posts automatically to WordPress</p>
            </div>
          </div>
        </section>

        <section className="how-it-works" id="how-it-works">
          <h2>How AutoBlogPilot Works</h2>
          <div className="steps-container">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Import Your Blog</h3>
              <p>Connect your WordPress blog and import existing content to analyze your writing style</p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>Configure Settings</h3>
              <p>Set your content preferences, SEO strategy, and target audience</p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>AI Creates Content</h3>
              <p>Our AI generates high-quality, original content matching your voice and style</p>
            </div>
            <div className="step">
              <div className="step-number">4</div>
              <h3>Automatic Publishing</h3>
              <p>Content is automatically scheduled and published to your blog</p>
            </div>
          </div>
        </section>

        <section className="pricing" id="pricing">
          <h2>Simple, Transparent Pricing</h2>
          <div className="pricing-grid">
            <div className="pricing-card starter">
              <h3>Free</h3>
              <div className="price">
                <div className="price-amount">
                  <span className="currency">$</span>
                  <span className="amount">0</span>
                  <span className="period">/month</span>
                </div>
              </div>
              <ul className="features-list">
                <li>✓ 2 AI-Generated Posts per Month</li>
                <li>✓ Auto SEO Optimization</li>
                <li>✓ AI Generated Images</li>
                <li>✓ Writing Style Analysis</li>
                <li>✓ Custom Scheduling</li>
                <li>✓ WordPress Auto-Posting</li>
              </ul>
              <button className="pricing-cta outline" onClick={handleGetStarted}>Get Started Free</button>
            </div>

            <div className="pricing-card starter">
              <h3>Starter</h3>
              <div className="price">
                <div className="price-amount">
                  <span className="currency">$</span>
                  <span className="amount">25</span>
                  <span className="period">/month</span>
                </div>
              </div>
              <ul className="features-list">
                <li>✓ 7 AI-Generated Posts per Month</li>
                <li>✓ Auto SEO Optimization</li>
                <li>✓ AI Generated Images</li>
                <li>✓ Writing Style Analysis</li>
                <li>✓ Custom Scheduling</li>
                <li>✓ WordPress Auto-Posting</li>
              </ul>
              <button className="pricing-cta outline" onClick={handleGetStarted}>Get Started</button>
            </div>

            <div className="pricing-card professional">
              <div className="popular-tag">Most Popular</div>
              <h3>Standard</h3>
              <div className="price">
                <div className="price-amount">
                  <span className="currency">$</span>
                  <span className="amount">50</span>
                  <span className="period">/month</span>
                </div>
              </div>
              <ul className="features-list">
                <li>✓ 15 AI-Generated Posts per Month</li>
                <li>✓ Auto SEO Optimization</li>
                <li>✓ AI Generated Images</li>
                <li>✓ Writing Style Analysis</li>
                <li>✓ Custom Scheduling</li>
                <li>✓ WordPress Auto-Posting</li>
              </ul>
              <button className="pricing-cta primary" onClick={handleGetStarted}>Get Started</button>
            </div>

            <div className="pricing-card premium">
              <h3>Premium</h3>
              <div className="price">
                <div className="price-amount">
                  <span className="currency">$</span>
                  <span className="amount">90</span>
                  <span className="period">/month</span>
                </div>
              </div>
              <ul className="features-list">
                <li>✓ 30 AI-Generated Posts per Month</li>
                <li>✓ Auto SEO Optimization</li>
                <li>✓ AI Generated Images</li>
                <li>✓ Writing Style Analysis</li>
                <li>✓ Custom Scheduling</li>
                <li>✓ WordPress Auto-Posting</li>
              </ul>
              <button className="pricing-cta outline" onClick={handleGetStarted}>Get Started</button>
            </div>

            <div className="pricing-card custom">
              <h3>Enterprise</h3>
              <div className="price">
                <div className="price-amount">
                  <span className="contact-text">Contact Us</span>
                </div>
              </div>
              <ul className="features-list">
                <li>✓ Custom Post Volume</li>
                <li>✓ Auto SEO Optimization</li>
                <li>✓ AI Generated Images</li>
                <li>✓ Writing Style Analysis</li>
                <li>✓ Custom Scheduling</li>
                <li>✓ WordPress Auto-Posting</li>
                <li>✓ Multiple Blog Management</li>
                <li>✓ API Access</li>
              </ul>
              <button className="pricing-cta outline" onClick={handleGetStarted}>Contact Sales</button>
            </div>
          </div>
        </section>

        <section className="cta-section">
          <h2>Ready to Automate Your Blog?</h2>
          <p>Get started today. No credit card required.</p>
          <button className="cta-primary" onClick={handleGetStarted}>
            Get Started for Free
          </button>
        </section>
      </main>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-links">
            <a href="/privacy-policy">Privacy Policy</a>
            <a href="/terms-of-service">Terms of Service</a>
            <a href="#contact">Contact</a>
          </div>
          <div className="footer-copyright">
            © {new Date().getFullYear()} AutoBlogPilot. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
